// src/components/admin/SuperadminPanel.tsx
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PublicIcon from "@mui/icons-material/Public";
import BugReportIcon from "@mui/icons-material/BugReport";
import SettingsIcon from "@mui/icons-material/Settings";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GavelIcon from "@mui/icons-material/Gavel";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BackupIcon from "@mui/icons-material/Backup";
import { useTranslation } from "../../i18n";
import { RootState } from "../../types";
import PlatformOverview from "./PlatformOverview";
import UserActivityTab from "./UserActivityTab";
import LandingAnalyticsTab from "./LandingAnalyticsTab";
import ErrorTicketsTab from "./ErrorTicketsTab";
import SystemConfigTab from "./SystemConfigTab";
import MonitoringDashboard from "./MonitoringDashboard";
import TierPricingEditor from "./TierPricingEditor";
import AdminLegalReviewPanel from "../billing/AdminLegalReviewPanel";
import AdminAccessTiersPanel from "./AdminAccessTiersPanel";
import AdminOrganizationsPanel from "./AdminOrganizationsPanel";
import AdminBackupsTab from "./AdminBackupsTab";
import { glass } from "../../theme/glassTokens";
import VideoGlassContainer from "../dashboards/VideoGlassContainer";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 24 }}>
    {value === index && children}
  </div>
);

const TAB_KEYS = ["overview", "activity", "analytics", "tickets", "config", "monitoring", "pricing", "compliance", "access", "organizations", "backups"] as const;

const SuperadminPanel: React.FC = () => {
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const tabFromUrl = searchParams.get("tab") || "overview";
  const initialTab = TAB_KEYS.indexOf(tabFromUrl as typeof TAB_KEYS[number]);
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : 0);

  if (!currentUser?.is_system_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams({ tab: TAB_KEYS[newValue] });
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        p: { md: 0.5, lg: 2,  xl: 3 },
        minHeight: 0,
      }}
    >
      {/* Same chrome as /environments, WelcomeHeroCard and BulkUploadResults.
          `"& > *": { flexShrink: 0 }` is REQUIRED, not cosmetic: the glass card is a
          flex COLUMN with a max height, so children default to flex-shrink:1 and get
          squeezed until they clip their own content. */}
      <VideoGlassContainer
        variant="dark"
        glassSx={{
          display: "flex",
          flexDirection: "column",
          "& > *": { flexShrink: 0 },
        }}
      >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }} gutterBottom>
          {t("admin.title", "Platform Administration")}
        </Typography>
        <Typography variant="body2" sx={{ color: textSecondary }}>
          {t("admin.subtitle", "Monitoring, analytics, and system configuration")}
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: glass.panelBorder }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: textSecondary,
              "&.Mui-selected": { color: textPrimary },
            },
          }}
        >
          <Tab icon={<DashboardIcon />} iconPosition="start" label={t("admin.tab_overview", "Overview")} />
          <Tab icon={<PeopleIcon />} iconPosition="start" label={t("admin.tab_activity", "User Activity")} />
          <Tab icon={<PublicIcon />} iconPosition="start" label={t("admin.tab_analytics", "Landing Analytics")} />
          <Tab icon={<BugReportIcon />} iconPosition="start" label={t("admin.tab_tickets", "Error Tickets")} />
          <Tab icon={<SettingsIcon />} iconPosition="start" label={t("admin.tab_config", "System Config")} />
          <Tab icon={<MonitorHeartIcon />} iconPosition="start" label={t("admin.tab_monitoring", "Monitoring")} />
          <Tab icon={<StorefrontIcon />} iconPosition="start" label={t("admin.tab_pricing", "Tier Pricing")} />
          <Tab icon={<GavelIcon />} iconPosition="start" label={t("admin.tab_compliance", "Compliance")} />
          <Tab icon={<ManageAccountsIcon />} iconPosition="start" label={t("admin.tab_access", "Access & Users")} />
          <Tab icon={<ApartmentIcon />} iconPosition="start" label={t("admin.tab_organizations", "Organizations")} />
          <Tab icon={<BackupIcon />} iconPosition="start" label={t("admin.tab_backups", "Backups")} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <PlatformOverview />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <UserActivityTab />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <LandingAnalyticsTab />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ErrorTicketsTab />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <SystemConfigTab />
      </TabPanel>
      <TabPanel value={activeTab} index={5}>
        <MonitoringDashboard />
      </TabPanel>
      <TabPanel value={activeTab} index={6}>
        <TierPricingEditor />
      </TabPanel>
      <TabPanel value={activeTab} index={7}>
        <AdminLegalReviewPanel />
      </TabPanel>
      <TabPanel value={activeTab} index={8}>
        <AdminAccessTiersPanel />
      </TabPanel>
      <TabPanel value={activeTab} index={9}>
        <AdminOrganizationsPanel />
      </TabPanel>
      <TabPanel value={activeTab} index={10}>
        <AdminBackupsTab />
      </TabPanel>
      </VideoGlassContainer>
    </Container>
  );
};

export default SuperadminPanel;
